import React, { useEffect, useRef, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/shadcn-ui';
import type { AuthFactorType, IAuthChallengeFactorOption, IAuthSmsOptedOut } from '@repo/types';

import { AuthErrorMessage } from './AuthErrorMessage';
import { CodeExpiryNotice } from './CodeExpiryNotice';
import { TWO_FACTOR_TICKET_TTL_SECONDS } from 'hooks/useCodeExpiry';
import { Input } from 'components/UI/Input';
import { useVerifyChallenge } from 'hooks/api/useWorkOSAuth';
import { workosAuthService } from 'services/workos-auth.service';

export interface TwoFactorChallengeVerifiedResult {
  isNewUser?: boolean;
  usedRecoveryCode?: boolean;
  remainingRecoveryCodes?: number;
}

export interface TwoFactorChallengeProps {
  ticket: string;
  factorType: AuthFactorType;
  onVerified: (result: TwoFactorChallengeVerifiedResult) => void;
  /** "Back to sign in" — the ticket is simply abandoned; it expires on its
   * own 5-minute TTL if never redeemed, nothing to explicitly cancel. */
  onBack: () => void;
}

/**
 * VRG-490 §2 batch 7 — the SIGN-IN-FLOW second-factor challenge, dropped
 * into whichever surface's login call returned `{challengeRequired: true,
 * ticket, factorType}`: `SignInBanner` (the primary `/sign-in` route) and
 * the embedded `SignIn` drawer (`pages/SignIn/index.tsx`, opened from
 * `CertificatePage`). NOT the enrollment/management surface — that is a
 * separate, deferred batch (see the task file's batch table).
 *
 * Reuses the SAME `InputOTP`/`InputOTPGroup`/`InputOTPSlot` trio
 * `SignInBanner`'s primary-factor OTP step already renders, rather than a
 * second hand-rolled 6-box row (§36).
 *
 * "Use a recovery code instead" swaps the OTP boxes for a single free-text
 * field, because a recovery code is NOT six digits — it's generated as
 * `XXXXX-XXXXX` (`generateRecoveryCode`, apps/api `libs/helpers/crypto.ts`).
 * Both submit through `useVerifyChallenge` -> `POST /auth/2fa/challenge/
 * verify`, which distinguishes a factor code from a recovery code by
 * looking the value up against both stores, not by a separate field.
 *
 * SMS opt-out: if the number replied STOP, no code was texted. The options call reports it
 * (`smsOptedOut`), and the screen then shows why, offers any other enrolled method, a
 * recovery code, or "Try again" once the person has texted START (a resend; the API clears
 * the block when the text goes through, and the resend cooldown applies).
 */
export const TwoFactorChallenge = ({ ticket, factorType: initialFactorType, onVerified, onBack }: TwoFactorChallengeProps) => {
  const [factorType, setFactorType] = useState<AuthFactorType>(initialFactorType);
  const [smsOptedOut, setSmsOptedOut] = useState<IAuthSmsOptedOut | null>(null);
  const [alternatives, setAlternatives] = useState<IAuthChallengeFactorOption[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * VRG-720 — when the current login ticket was issued, for its visible
   * 5-minute deadline (`CHALLENGE_TTL_SECONDS`).
   *
   * Seeded at MOUNT because the ticket is minted by the `magic-auth/verify`
   * call that produced this screen — the two are a round-trip apart. Reset
   * inside `applyState` rather than at each call site, because that is the one
   * place every ticket refresh lands: the initial options fetch, a resend
   * (`handleTryAgain`), and a factor switch (`handleSwitch`) all pass through
   * it. `TwoFactorChallengeService.refresh` extends the ticket on those, capped
   * at `CHALLENGE_MAX_LIFETIME_SECONDS` (15 min) overall, so the cap is the one
   * thing this countdown does not model — a user who resends three times sees
   * 5:00 again on a ticket with less than that left. Worth knowing; not worth a
   * second clock, since the ticket's own 404 is authoritative and already
   * handled below.
   *
   * Note this bounds the TICKET, not the code. A TOTP code rotates every 30s on
   * the user's own device and has no server-side deadline for us to show; the
   * ticket it is spent against dies at 5 minutes regardless of factor, so that
   * is the deadline that actually governs both branches.
   */
  const [ticketIssuedAt, setTicketIssuedAt] = useState<number | null>(() => Date.now());

  const applyState = (state: { factorType: AuthFactorType; factorId: string; options: IAuthChallengeFactorOption[]; smsOptedOut?: IAuthSmsOptedOut }) => {
    setFactorType(state.factorType);
    setTicketIssuedAt(Date.now());
    setSmsOptedOut(state.smsOptedOut ?? null);
    setAlternatives(state.options.filter((option) => option.factorId !== state.factorId));
  };

  useEffect(() => {
    let cancelled = false;
    workosAuthService.getChallengeOptions(ticket).then((state) => {
      if (!cancelled && state) applyState(state);
    });
    return () => {
      cancelled = true;
    };
  }, [ticket]);

  const textsBlocked = factorType === 'sms' && Boolean(smsOptedOut);

  const handleTryAgain = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    setError(null);
    setNotice(null);
    const result = await workosAuthService.resendChallengeCode(ticket);
    setIsRetrying(false);
    if (result.state) {
      applyState(result.state);
      setNotice('Texts are back on. We sent you a code.');
      return;
    }
    setError(
      result.errorCode === 'SMS_RECIPIENT_OPTED_OUT'
        ? 'Texts are still blocked. Text START first, then try again.'
        : result.error ?? 'Could not send a code. Please try again.'
    );
  };

  const handleSwitch = async (option: IAuthChallengeFactorOption) => {
    if (isRetrying) return;
    setIsRetrying(true);
    setError(null);
    setNotice(null);
    const result = await workosAuthService.switchChallengeFactor(ticket, option.factorId);
    setIsRetrying(false);
    if (result.state) {
      applyState(result.state);
      setCode('');
      setUseRecoveryCode(false);
      return;
    }
    setError(result.error ?? 'Could not switch method. Please try again.');
  };

  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A 404 means the ticket itself is dead (expired / burned after 5
  // attempts) — resubmitting cannot succeed, so Verify stays disabled and
  // the only path forward is "Back to sign in".
  const [expired, setExpired] = useState(false);
  const verifyChallenge = useVerifyChallenge();
  const inFlightRef = useRef(false);

  // §54 — Submit stays dead until there's something valid to submit. A
  // recovery code has no fixed length (unlike the 6-digit factor code), so
  // "non-empty" is the validity bar for that branch.
  const isComplete = useRecoveryCode ? recoveryCode.trim().length > 0 : code.length === 6;

  const handleSubmit = async () => {
    if (inFlightRef.current || !isComplete || expired) return;

    inFlightRef.current = true;
    setError(null);

    try {
      const value = useRecoveryCode ? recoveryCode.trim() : code;
      const result = await verifyChallenge.mutateAsync({ ticket, code: value });
      onVerified({
        isNewUser: result.isNewUser,
        usedRecoveryCode: result.usedRecoveryCode,
        remainingRecoveryCodes: result.remainingRecoveryCodes,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      const isExpired = status === 404;
      setExpired(isExpired);
      setError(
        err?.response?.data?.message ||
          (isExpired
            ? 'This code has expired or too many attempts were made. Please sign in again.'
            : 'Incorrect code. Please try again.')
      );
      if (!useRecoveryCode) setCode('');
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <div>
      <h2 className='mb-4 text-[24px] leading-[32px] font-[400]'>Verify it's you</h2>

      <Typography variant='body2' sx={{ mb: 2, lineHeight: 1.5 }} data-testid={!useRecoveryCode && textsBlocked ? 'sms-opted-out-message' : undefined}>
        {useRecoveryCode
          ? 'Enter one of your saved recovery codes.'
          : textsBlocked && smsOptedOut
          ? smsOptedOut.message
          : factorType === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'We texted a 6-digit verification code to your phone.'}
      </Typography>

      {useRecoveryCode ? (
        <Input
          type='text'
          value={recoveryCode}
          onChange={(value) => {
            setRecoveryCode(value);
            if (error) setError(null);
          }}
          placeholder='XXXXX-XXXXX'
          sx={{ mb: 2, '& input': { paddingLeft: '8px' } }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!inFlightRef.current) handleSubmit();
            }
          }}
          autoFocus
        />
      ) : textsBlocked ? null : (
        <div className='mb-4'>
          <InputOTP
            onChange={(value: string) => {
              setCode(value.replace(/\D/g, '').slice(0, 6));
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!inFlightRef.current) handleSubmit();
              }
            }}
            value={code}
            maxLength={6}
            id='two-factor-code'
            required
            autoFocus
            containerClassName='w-full'
          >
            <InputOTPGroup className='flex w-full gap-2 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border'>
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={0} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={1} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={2} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={3} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={4} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      )}

      <AuthErrorMessage message={error} />
      {!error && notice && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {notice}
        </Typography>
      )}

      {/*
        Suppressed once the SERVER has said the ticket is dead: `expired` is set
        from an actual 404, and its message is already on screen above. Showing
        a clock-derived "that code has expired" beside it would be the same news
        twice, from the weaker source.
      */}
      {!expired && (
        <CodeExpiryNotice
          issuedAt={ticketIssuedAt}
          ttlSeconds={TWO_FACTOR_TICKET_TTL_SECONDS}
          resendLabel='Go back and sign in again'
        />
      )}

      <Stack spacing={2}>
        {textsBlocked && !useRecoveryCode ? (
          <Button variant='contained' fullWidth onClick={handleTryAgain} disabled={isRetrying} sx={{ paddingX: 2 }}>
            {isRetrying ? 'Trying...' : 'Try again'}
          </Button>
        ) : (
          <Button
            variant='contained'
            fullWidth
            onClick={handleSubmit}
            disabled={verifyChallenge.isPending || !isComplete || expired}
            sx={{ paddingX: 2 }}
          >
            {verifyChallenge.isPending ? 'Verifying...' : 'Verify'}
          </Button>
        )}
        {/* Only offered while texts are blocked — the general "use another method" list is
            the platform's; the owner screen keeps its simpler form otherwise. */}
        {textsBlocked &&
          !useRecoveryCode &&
          alternatives.map((option) => (
            <Button
              key={option.factorId}
              variant='text'
              fullWidth
              onClick={() => handleSwitch(option)}
              disabled={verifyChallenge.isPending || isRetrying || option.optedOut}
              sx={{ paddingX: 2 }}
            >
              {option.type === 'totp' ? 'Use your authenticator app instead' : 'Text a code instead'}
            </Button>
          ))}
        <Button
          variant='text'
          fullWidth
          onClick={() => {
            setUseRecoveryCode((v) => !v);
            setError(null);
            setExpired(false);
          }}
          disabled={verifyChallenge.isPending}
          sx={{ paddingX: 2 }}
        >
          {useRecoveryCode ? 'Use your authenticator or SMS code instead' : 'Use a recovery code instead'}
        </Button>
        <Button variant='outlined' fullWidth onClick={onBack} disabled={verifyChallenge.isPending} sx={{ paddingX: 2 }}>
          Back to sign in
        </Button>
      </Stack>
    </div>
  );
};

export default TwoFactorChallenge;
